import * as logger from "../utils/logger";
import { handleApi } from "../utils/error";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import * as common from "./common";
import { getCurrentVehicle, clearCurrentVehicle } from "../utils/device";
import { isEqual, extend } from "../utils/vanilla";
import { IShipment } from "../model/Shipment";
import BaseSync, { IFeedResult, ISyncAddSet, MIN_VERSION, NumberOfSyncRetries, TSyncMethod } from "./base";
import { IModifiedType } from "./sync.types";
import { FeedTypeKey } from "./common";
import { ShipmentActions } from "../store/shipment/actions";
import { getShipments, getUpdatedGroupedShipments } from "../model/Shipments";
import { isArgumentException, isDuplicateException, isOverLimitException, isRelationViolatedException } from "../utils/errorHandler/error.types";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import { NODEVICEID } from "../model/DutyStatusLog";
import { IApiError, TMultiCallParam } from "../utils/api.interfaces";
import Api from "../services/api";

export interface IShipmentLogSearch {
    typeName: "ShipmentLog";
    search: {
        deviceSearch?: { id: string };
        userSearch?: { id: string };
        fromDate: string;
    };
    fromVersion?: string | null;
}

const CYCLE_MULTIPLIER = 2;

const FK_SHIPMENTLOG_VEHICLE = "fk_shipmentlog_vehicle";
const FK_SHIPMENTLOG_USERS = "fk_shipmentlog_users";

    export default class ShipmentSync extends BaseSync {
        defaults(): { name: string } {
            return {
                name: "Sync: Shipment"
            };
        }

        synchronize(progressCb: common.TSyncCallback, force: boolean, profilingBatcher?: PerformanceProfileBatcher): Promise<void> {
            return this.createProfiledSync(MeasureName.SyncShipment, () => {
                return new Promise<void>(resolve => {
                    this.fetchData(force)
                        .then(() => {
                            progressCb("Shipments");
                            resolve();
                        })
                        .catch((error: IApiError) => {
                            progressCb("Shipments", error);
                            resolve();
                        });
                });
            }, profilingBatcher);
        }

        /**
         * Will throw an error causing caller to reject promise
         * @param obj Shipment object
         */
        checkValue(obj: IShipment) {
            if (!obj.activeFrom) {
                throw new Error("Must have an activeFrom date");
            }

            if (obj.activeTo && obj.activeFrom > obj.activeTo) {
                throw new Error("activeFrom must be older than activeTo");
            }

            if (!obj.driver) {
                throw new Error("Must specify a driver");
            }

            if (!obj.documentNumber && (!obj.shipperName || !obj.commodity)) {
                throw new Error("Must have a document number or a shipper name and commodity");
            }
        }

        private setLogs(logsToSave: IIdDictionary<IShipment>, syncedDeviceIds?: Set<string>) {
            if (!logsToSave) {
                return;
            }
            const updatedShipments = getUpdatedGroupedShipments(logsToSave);
            const hasKeys = Object.keys(updatedShipments).length > 0;

            if (hasKeys) {
                Object.keys(updatedShipments).forEach(deviceId => {
                    this.store.dispatch(ShipmentActions.setShipments({
                        keyId: deviceId,
                        shipments: updatedShipments[deviceId] || {}
                    }));
                });
            } else {
                // In device-based path, clear only the current vehicle's group so other
                // devices' shipments (e.g. active shipments on a different vehicle) are preserved.
                // When the current vehicle was already cleared (FK_shipmentlog_vehicle), fall back
                // to clearing all shipments.
                const currentVehicleId = getCurrentVehicle(this.store)?.id;
                if (currentVehicleId) {
                    this.store.dispatch(ShipmentActions.setShipments({ keyId: currentVehicleId, shipments: {} }));
                } else {
                    this.store.dispatch(ShipmentActions.clearAllShipments());
                }
            }

            // Clear any device groups that were part of the sync but ended up empty in the
            // result (e.g. a modified shipment from another device was deleted during sync).
            syncedDeviceIds?.forEach(deviceId => {
                if (!updatedShipments[deviceId]) {
                    this.store.dispatch(ShipmentActions.setShipments({ keyId: deviceId, shipments: {} }));
                }
            });
        }

        private mergeShipmentLogs(serverLogs: IShipment[], force: boolean): IIdDictionary<IShipment> {
            let localLogs = this.getLocalShipmentsLogs();

            this.removeUnmodifiedLogsOnForce(localLogs, force);
            this.mergeServerLogsIntoLocal(serverLogs, localLogs);

            return localLogs;
        }

        private syncShipments(localLogs: IIdDictionary<IShipment>, force: boolean): Promise<void> {
            return new Promise<void>((resolve, reject) => {
                const currentUser = this.api.getUser();
                const preSyncDeviceIds = new Set<string>(
                    Object.keys(localLogs)
                        .map(k => { const s = localLogs[k]; return typeof s.device === "object" ? s.device?.id : s.device; })
                        .filter((id): id is string => !!id)
                );
                let updateLogStorage = () => {
                    this.setLogs(localLogs, preSyncDeviceIds);
                    dispatcher.trigger(DispatcherEvents.SyncShipments);
                };

                const calls = this.prepareShipmentSyncCalls(localLogs);

                if (calls.length > 0) {
                    const handleSyncSuccess = (results: (string | undefined)[]) => {
                        this.resolveSyncedEntities<IShipment>(calls, results, (entity: IShipment) => localLogs[entity.id]);
                        updateLogStorage();
                    };

                    logger.log(logger.Source.SYNC, common.formatCallForLogger("Shipments", calls), currentUser);
                    this.api.multiCall(calls, (results: (string | undefined)[]) => {
                        handleSyncSuccess(results);
                        resolve();
                    }, (errorObject: IApiError) => {
                        if ((errorObject.data?.requestIndex ?? 0) > 0) {
                            this.handlePartialMultiCallSuccess(errorObject, calls, (entity: IShipment) => localLogs[entity.id]);
                            updateLogStorage();
                        }

                        const errorCallback = () => {
                            dispatcher.trigger(DispatcherEvents.SyncShipments);
                            reject(errorObject);
                        };

                        if (isOverLimitException(errorObject)) {
                            const succeededCount = errorObject.data?.requestIndex ?? 0;
                            const failedCount = calls.length - succeededCount;
                            logger.error(
                                logger.Source.SYNC,
                                `OverLimitException during Shipment sync. Succeeded: ${ succeededCount }, Failed: ${ failedCount }`,
                                currentUser
                            );
                            errorCallback();
                            return;
                        }

                        const requestIndex = errorObject.data?.requestIndex;
                        const call = requestIndex !== undefined ? calls[requestIndex] : undefined;
                        const log = call?.[1]?.entity;
                        const hasShipmentLocally = !!log && localLogs.hasOwnProperty(log.id);

                        const continueCallback = () => {
                            this.setLogs(localLogs, preSyncDeviceIds);
                            this.syncShipments(localLogs, force).then(resolve, reject);
                        };

                        this.handleSyncError(errorObject, localLogs, log, hasShipmentLocally, continueCallback, errorCallback);
                    });
                } else {
                    updateLogStorage();
                    resolve();
                }
            });
        }

        private getFeedVersion(api: Api): string | null {
            return common.getFeedVersion(api, this.store, FeedTypeKey.SHIPMENT);
        }

        private setFeedVersion(api: Api, version: string) {
            common.setFeedVersion(api, this.store, FeedTypeKey.SHIPMENT, version);
        }

        private load(force: boolean): Promise<{ shipments: IShipment[]; version: string }> {
            const shipments = getShipments(this.store);
            const method = !force && shipments && Object.keys(shipments).length > 0 && this.getFeedVersion(this.api) ? "GetFeed" : "Get";
            const logMessage = method === "GetFeed"
                ? (count: number) => `Downloaded ${ count } shipment logs from feed`
                : (count: number) => `Downloaded ${ count } shipment logs`;

            return this.loadData(method, logMessage);
        }

        private fetchData(force: boolean): Promise<void> {
            return new Promise((resolve, reject) => {
                let newFeedVersion = "";

                this.load(force)
                    .then(({ shipments, version }) => {
                        newFeedVersion = version;

                        const localLogs = this.mergeShipmentLogs(shipments, force);
                        return this.syncShipments(localLogs, force);
                    })
                    .then(() => {
                        if (newFeedVersion && newFeedVersion !== MIN_VERSION) {
                            this.setFeedVersion(this.api, newFeedVersion);
                        }
                        resolve();
                    })
                    .catch((error: IApiError) => {
                        handleApi(error);
                        reject(error);
                    });
            });
        }

        private readonly getShipmentDeviceId = (shipmentLog: IShipment): string | undefined => {
            if (!shipmentLog?.device) {
                return undefined;
            }
            return typeof shipmentLog.device === "object" ? shipmentLog.device.id : shipmentLog.device;
        };

        private handleSyncError(
            errorObject: IApiError,
            localLogs: IIdDictionary<IShipment>,
            log: IShipment | undefined,
            hasShipmentLocally: boolean,
            continueCallback: () => void,
            errorCallback: () => void
        ) {
            if (isDuplicateException(errorObject) && log && hasShipmentLocally) {
                // This log is already in the database, make it a "Set" and sync again
                localLogs[log.id].modified = "Set";
                continueCallback();
            } else if (isRelationViolatedException(errorObject) && log && hasShipmentLocally) {
                this.handleForeignKeyViolation(errorObject, localLogs, log, continueCallback, errorCallback);
            } else if (isArgumentException(errorObject) && log && hasShipmentLocally) {
                logger.log(
                    logger.Source.SYNC,
                    `Shipment Sync ArgumentException: CallLog ${ JSON.stringify(log) } Message: ${ errorObject.message }`,
                    this.api.getUser()
                );
                delete localLogs[log.id];
                continueCallback();
            } else {
                errorCallback();
            }
        }

        private handleForeignKeyViolation(
            errorObject: IApiError,
            localLogs: IIdDictionary<IShipment>,
            log: IShipment,
            continueCallback: () => void,
            errorCallback: () => void
        ) {
            const fkConstraint = common.getForeignKeyConstraint(errorObject)?.toLowerCase();

            if (!fkConstraint) {
                delete localLogs[log.id];
                continueCallback();
                return;
            }

            switch (fkConstraint) {
                case FK_SHIPMENTLOG_VEHICLE:
                    this.handleVehicleForeignKeyViolation(localLogs, log);
                    continueCallback();
                    break;

                case FK_SHIPMENTLOG_USERS:
                    delete localLogs[log.id];
                    continueCallback();
                    break;

                default:
                    // unhandled foreign key exception, fail out
                    errorCallback();
                    break;
            }
        }

        private handleVehicleForeignKeyViolation(localLogs: IIdDictionary<IShipment>, log: IShipment) {
            const currentVehicle = getCurrentVehicle();
            const removedVehicle = log?.device && typeof log.device !== "string" && log.device.id ? log.device : null;

            if (currentVehicle && removedVehicle && currentVehicle.id === removedVehicle.id) {
                clearCurrentVehicle();
                dispatcher.trigger(DispatcherEvents.VehicleRemoved);
            }

            if (removedVehicle) {
                Object.keys(localLogs).forEach(logId => {
                    const localLog = localLogs[logId];
                    if (localLog?.device && typeof localLog.device !== "string" && localLog.device.id === removedVehicle.id) {
                        localLog.device = undefined;
                    }
                });
            }
        }

        private getLocalShipmentsLogs(): IIdDictionary<IShipment> {
            let localLogs: IIdDictionary<IShipment> = {};

            const currentVehicleId = getCurrentVehicle(this.store)?.id;
            // clear shipments that don't belong to the current vehicle keeping modified (scenario when shipment detached offline and vehicle changed)
            if (currentVehicleId) {
                const allShipments = this.store.getState().shipments;
                Object.keys(allShipments).forEach(deviceId => {
                    Object.keys(allShipments[deviceId]).forEach(shipmentId => {
                        const shipment = allShipments[deviceId][shipmentId];
                        const shipmentDeviceId = this.getShipmentDeviceId(shipment);
                        if (shipmentDeviceId === currentVehicleId || shipment.modified !== undefined) {
                            localLogs[shipmentId] = shipment;
                        }
                    });
                });
            }

            return localLogs;
        }

        private removeUnmodifiedLogsOnForce(localLogs: IIdDictionary<IShipment>, force: boolean) {
            if (force) {
                let shipmentIds = Object.keys(localLogs);
                shipmentIds.forEach(shipmentId => {
                    if (!localLogs[shipmentId].modified) {
                        delete localLogs[shipmentId];
                    }
                });
            }
        }

        private mergeServerLogsIntoLocal(serverLogs: IShipment[], localLogs: IIdDictionary<IShipment>) {
            const currentUser = this.api.getUser();

            serverLogs.forEach(serverLog => {
                // Server log exists locally
                if (localLogs[serverLog.id]) {
                    // Conflict resolution
                    const local = localLogs[serverLog.id];
                    const previousState: IShipment | undefined = local.previousState;
                    const modifiedState: IModifiedType | undefined = local.modified;

                    /*
                     Four scenarios:
                     local changed, server same => take local
                     local changed, server changed => conflict if local was "edit", merge local and server if local was "add"
                     local same, server changed => take server
                     local same, server same => do nothing
                     */

                    if (modifiedState) {
                        //Local changed
                        if (!isEqual(previousState, serverLog)) {
                            if (modifiedState === "Add") {
                                // Server has a different version, merge them
                                logger.log(logger.Source.SYNC, "Merged local Shipment with server", currentUser);
                                localLogs[serverLog.id] = common.merge(localLogs[serverLog.id], serverLog, { modified: "Set" });
                            } else {
                                logger.error(logger.Source.SYNC, `SYNC: Shipment conflict (both server and client changed). Local ID: ${ local.id }, Server ID: ${ serverLog.id }`);
                            }
                        }
                    } else {
                        // Local same
                        if (!isEqual(local, serverLog)) {
                            logger.log(logger.Source.SYNC, "Updated local Shipment from server", currentUser);
                            localLogs[serverLog.id] = serverLog;
                        }
                    }
                } else {
                    localLogs[serverLog.id] = serverLog;
                }
            });
        }

        private prepareShipmentSyncCalls(localLogs: IIdDictionary<IShipment>): [IModifiedType, ISyncAddSet<IShipment>][] {
            const calls: [IModifiedType, ISyncAddSet<IShipment>][] = [];

            const tryAssignToCurrentVehicle = (shipmentLog: IShipment) => {
                const shipmentDeviceId = this.getShipmentDeviceId(shipmentLog);
                if (shipmentDeviceId === NODEVICEID) {
                    const currentVehicleId = getCurrentVehicle(this.store)?.id;
                    if (currentVehicleId) {
                        shipmentLog.device = { id: currentVehicleId };
                        shipmentLog.modified = "Set";
                    }
                }
            };

            Object.keys(localLogs).forEach(localLogKey => {
                let log = localLogs[localLogKey];

                if (log.id === null) {
                    throw new Error("Attempting to sync a record without an ID");
                }

                tryAssignToCurrentVehicle(log);

                if (log.modified) {
                    let sanitizedLog: IShipment = extend({}, log);
                    this.removeClientSideProperties(sanitizedLog);

                    if (log.modified === "Add" || log.modified === "Set") {
                        if (log._syncCounter === undefined) {
                            log._syncCounter = NumberOfSyncRetries;
                        }
                        if (log._syncCounter > 0) {
                            log._syncCounter--;
                            calls.push([log.modified, {
                                typeName: "ShipmentLog",
                                entity: sanitizedLog
                            }]);
                        }
                    }
                }
            });

            return calls;
        }

        private getShipmentSearch(method: TSyncMethod, searchEntity: IIdEntity | null | undefined, searchType: "device" | "user" = "device"): IShipmentLogSearch | null {
            if (!searchEntity?.id) {
                return null;
            }

            const searchKey = searchType === "device" ? "deviceSearch" : "userSearch";
            return {
                typeName: "ShipmentLog",
                search: {
                    [searchKey]: { id: searchEntity.id },
                    fromDate: this.hosProcess.getRetentionStartDate(CYCLE_MULTIPLIER).toISOString()
                },
                ...(method === "GetFeed" ? { fromVersion: this.getFeedVersion(this.api) } : undefined)
            };
        }

        private loadData(method: TSyncMethod, getLogMessage: (count: number) => string): Promise<{ shipments: IShipment[]; version: string }> {
            return new Promise<{ shipments: IShipment[]; version: string }>((resolve, reject) => {
                const search = this.getShipmentSearch(method, getCurrentVehicle(this.store));

                const calls: TMultiCallParam[] = [];
                search && calls.push([method, search]);

                const currentUser = this.api.getUser();
                const unassignedVehicleShipmentsSearch = this.getShipmentSearch(method, currentUser, "user");
                unassignedVehicleShipmentsSearch && calls.push([method, unassignedVehicleShipmentsSearch]);

                const getLogsFromCallResults = (results: IShipment[] | IFeedResult<IShipment>): IShipment[] => {
                    const data = method === "GetFeed" ? (results as IFeedResult<IShipment>).data : (results as IShipment[]);
                    return data || [];
                };

                const processShipmentsData = (logs: IShipment[], version: string) => {
                    logger.log(logger.Source.SYNC, getLogMessage(logs.length), this.api.getUser());
                    resolve({ shipments: logs, version });
                };

                const handleError = (error: IApiError) => {
                    if (method === "GetFeed") {
                        handleApi(error);
                    }
                    reject(error);
                };

                if (calls.length > 0) {
                    this.api.multiCall(calls, (results: (IShipment[] | IFeedResult<IShipment>)[]) => {
                        const shipmentsByDeviceData = getLogsFromCallResults(results[0]);
                        const shipmentsByUserData = results[1] ? getLogsFromCallResults(results[1]).filter((shipmentLog: IShipment) => {
                            const deviceId = this.getShipmentDeviceId(shipmentLog);
                            return deviceId === null || deviceId === NODEVICEID;
                        }) : [];
                        const allLogs = [...shipmentsByDeviceData, ...shipmentsByUserData];
                        let version: string;
                        if (method === "GetFeed") {
                            const version1 = (results[0] as IFeedResult<IShipment>).toVersion || MIN_VERSION;
                            const version2 = results[1] ? (results[1] as IFeedResult<IShipment>).toVersion || MIN_VERSION : MIN_VERSION;
                            version = version1 > version2 ? version1 : version2;
                        } else {
                            version = this.getLatestVersion(allLogs);
                        }
                        processShipmentsData(allLogs, version);
                    }, (error: IApiError) => {
                        handleError(error);
                    });
                } else {
                    resolve({ shipments: [], version: MIN_VERSION });
                }
            });
        }
    }